import { useTranslation } from 'react-i18next';
import useLoginOtp from '../../hooks/useLoginOtp';
import useCustomOtpInput from '../../hooks/useCustomOtpInput';

const LoginOtp = ({ setLoginOtp, loginOtp, formData }) => {
  const { t } = useTranslation('common');

  const {
    errors,
    loadingOtp,
    otpValue,
    timeLeft,
    wrongAttempts,
    formatTime,
    handleResendOtp,
    handleLoginOtp,
    setOtpValue,
    setErrors,
  } = useLoginOtp({ setLoginOtp, loginOtp, formData });

  return (
    <>
      <div className="w-full mt-5">
        <h1 className="font-semibold text-2xl">{t('otp_verification')}</h1>
      </div>
      <div className="w-full mt-2">
        <p className="text-base font-normal text-slate-500">
          {t('otp_verification_instruction')}
        </p>
      </div>
      <div className="w-full my-4">
        <div className="flex justify-center items-center gap-2">
          <span className="text-base font-normal text-slate-500">
            {t('otp_verification_before')} :
          </span>
          <span
            className={`text-base font-semibold ${timeLeft === 0 ? 'text-red-500' : 'text-slate-700'}`}
          >
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      <div className="w-full mb-2">
        <CustomOTPInput
          length={6}
          value={otpValue}
          onChange={setOtpValue}
          placeholder="0"
          timeLeft={timeLeft}
          handleLoginOtp={handleLoginOtp}
          error={errors?.error}
          setErrors={setErrors}
        />
      </div>
      {timeLeft === 0 && (
        <div className="w-full justify-center items-center flex mb-2">
          <span className="text-slate-500 text-sm">
            {t('otp_verification_expired')}
          </span>
        </div>
      )}
      {errors?.otpValue && (
        <div className="w-full justify-center items-center flex mb-2">
          <span className="text-red-500 text-sm">{t(errors?.otpValue)}</span>
        </div>
      )}
      {wrongAttempts === 3 && (
        <div className="w-full justify-center items-center flex mb-2">
          <span className="text-red-500 text-sm">{t(errors?.maxAttempts)}</span>
        </div>
      )}
      {errors?.error && (
        <div className="w-full justify-center items-center flex mb-2">
          <span className="text-red-500 text-sm">{t(errors?.error)}</span>
        </div>
      )}

      <div className="w-full flex flex-col justify-center text-center mt-3 gap-4 my-4 px-2">
        <button
          type="button"
          onClick={handleLoginOtp}
          disabled={loadingOtp || timeLeft === 0 || otpValue.length < 6}
          className="flex w-full bg-blue-500 justify-center rounded-md bg-con-brand px-3 py-2 text-medium font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {loadingOtp ? (
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 mx-3 my-0.5 border-white border-solid m-auto" />
          ) : (
            t('verification')
          )}
        </button>
      </div>
      <div className="w-full mt-3 flex justify-center">
        <button
          type="button"
          onClick={handleResendOtp}
          disabled={timeLeft !== 0}
          className={`text-sm font-semibold ${timeLeft === 0 ? 'text-blue-600 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
        >
          {t('otp_verification_resend')}
        </button>
      </div>
    </>
  );
};

function CustomOTPInput({
  length = 6,
  value = '',
  onChange,
  placeholder = '0',
  timeLeft,
  handleLoginOtp,
  error,
  setErrors,
}) {
  const { inputRefs, handleInputChange, handleKeyDown, handlePaste } =
    useCustomOtpInput({
      length,
      value,
      onChange,
      setErrors,
      handleLoginOtp,
    });

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          disabled={timeLeft === 0}
          key={index}
          ref={inputRefs[index]}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength="1"
          value={value[index] || ''}
          placeholder={placeholder}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`w-16 h-16 text-center border-2 rounded-lg border-gray-300 focus:border-blue-500 focus:outline-none text-5xl font-medium ${timeLeft === 0 ? 'bg-gray-200 opacity-30 border-gray-500 cursor-not-allowed' : ''} ${error ? 'border-red-300 text-red-700' : ''}`}
        />
      ))}
    </div>
  );
}

export default LoginOtp;
